import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import Eyebrow from "@/components/Eyebrow";
import SEO from "@/components/SEO";
import api from "@/lib/api";
import { toast } from "sonner";

const PACKAGES = [
  { id: "seed", amount: 25, label: "Seed", desc: "Support a learner for a month" },
  { id: "grow", amount: 50, label: "Grow", desc: "Fund a health outreach visit" },
  { id: "weave", amount: 100, label: "Weave", desc: "Sponsor a youth football camp" },
  { id: "cloth", amount: 250, label: "Whole Cloth", desc: "Underwrite a full program week" },
];

function DonationForm() {
  const [pkg, setPkg] = useState("weave");
  const [customAmount, setCustomAmount] = useState("");
  const [donorName, setDonorName] = useState("");
  const [donorEmail, setDonorEmail] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleDonate(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        origin_url: window.location.origin,
        donor_name: donorName || null,
        donor_email: donorEmail || null,
        note: note || null,
      };
      if (pkg === "custom") {
        const amt = parseFloat(customAmount);
        if (!amt || amt < 1) {
          toast.error("Please enter an amount of at least $1");
          setLoading(false);
          return;
        }
        payload.custom_amount = amt;
      } else {
        payload.package_id = pkg;
      }
      const { data } = await api.post("/donations/create-session", payload);
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start donation. Try again.");
      setLoading(false);
    }
  }

  return (
    <form id="donate" onSubmit={handleDonate} className="bg-white border border-ink/10 rounded-[14px] p-8" data-testid="donate-form">
      <span className="text-xs uppercase tracking-[0.14em] font-bold text-palm">Give today</span>
      <h3 className="font-display text-2xl mt-2 mb-5">Choose a thread to strengthen.</h3>

      <div className="grid grid-cols-2 gap-3 mb-4">
        {PACKAGES.map((p) => {
          const active = pkg === p.id;
          return (
            <button
              type="button"
              key={p.id}
              data-testid={`donate-pkg-${p.id}`}
              onClick={() => setPkg(p.id)}
              className={`text-left rounded-[12px] px-4 py-3 border-2 transition ${
                active ? "border-palm bg-palm text-white" : "border-ink/15 bg-white text-ink hover:border-palm"
              }`}
            >
              <div className="font-display text-lg">${p.amount}</div>
              <div className={`text-xs font-semibold ${active ? "text-white/90" : "text-ink/60"}`}>{p.label}</div>
              <div className={`text-xs mt-1 ${active ? "text-white/85" : "text-ink/60"}`}>{p.desc}</div>
            </button>
          );
        })}
      </div>

      <div className="mb-4">
        <button
          type="button"
          data-testid="donate-pkg-custom"
          onClick={() => setPkg("custom")}
          className={`w-full rounded-[12px] px-4 py-3 border-2 text-left transition ${
            pkg === "custom" ? "border-palm bg-palm/5" : "border-ink/15 bg-white hover:border-palm"
          }`}
        >
          <div className="font-semibold text-sm mb-1">Custom amount (USD)</div>
          <input
            type="number"
            min="1"
            step="1"
            placeholder="Enter amount"
            value={customAmount}
            onChange={(e) => { setCustomAmount(e.target.value); setPkg("custom"); }}
            data-testid="donate-custom-input"
            className="w-full rounded-md border border-ink/15 px-3 py-2 focus:outline-none focus:border-palm text-ink bg-white"
          />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <input type="text" placeholder="Your name (optional)" value={donorName} onChange={(e) => setDonorName(e.target.value)} data-testid="donate-name-input" className="rounded-md border border-ink/15 px-3 py-2.5 focus:outline-none focus:border-palm" />
        <input type="email" placeholder="Your email (optional)" value={donorEmail} onChange={(e) => setDonorEmail(e.target.value)} data-testid="donate-email-input" className="rounded-md border border-ink/15 px-3 py-2.5 focus:outline-none focus:border-palm" />
      </div>
      <textarea placeholder="Note to HOFA (optional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="donate-note-input" className="w-full rounded-md border border-ink/15 px-3 py-2.5 focus:outline-none focus:border-palm mb-4" rows={2} />

      <button type="submit" disabled={loading} data-testid="donate-submit-btn" className="w-full bg-palm text-white font-bold rounded-full px-6 py-3.5 hover:shadow-xl hover:-translate-y-0.5 transition disabled:opacity-60">
        {loading ? "Redirecting to Stripe…" : "Give securely via Stripe →"}
      </button>
      <p className="text-xs text-ink/55 mt-3">Payments handled by Stripe in test mode. Use card <code className="bg-ink/5 px-1 rounded">4242 4242 4242 4242</code> to try it.</p>
    </form>
  );
}

function VolunteerForm() {
  const [f, setF] = useState({ name: "", email: "", phone: "", location: "", skills: "", availability: "", message: "" });
  const [loading, setLoading] = useState(false);
  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/volunteer", f);
      toast.success("Thank you! HOFA will be in touch.");
      setF({ name: "", email: "", phone: "", location: "", skills: "", availability: "", message: "" });
    } catch (err) {
      toast.error("Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  }
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  return (
    <form id="volunteer" onSubmit={submit} className="bg-white border border-ink/10 rounded-[14px] p-8" data-testid="volunteer-form">
      <span className="text-xs uppercase tracking-[0.14em] font-bold text-mango-deep">Volunteer</span>
      <h3 className="font-display text-2xl mt-2 mb-5">Lend your skills.</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <input required placeholder="Full name" value={f.name} onChange={set("name")} data-testid="vol-name" className="rounded-md border border-ink/15 px-3 py-2.5 focus:outline-none focus:border-palm" />
        <input required type="email" placeholder="Email" value={f.email} onChange={set("email")} data-testid="vol-email" className="rounded-md border border-ink/15 px-3 py-2.5 focus:outline-none focus:border-palm" />
        <input placeholder="Phone" value={f.phone} onChange={set("phone")} data-testid="vol-phone" className="rounded-md border border-ink/15 px-3 py-2.5 focus:outline-none focus:border-palm" />
        <input placeholder="Location (city, country)" value={f.location} onChange={set("location")} data-testid="vol-location" className="rounded-md border border-ink/15 px-3 py-2.5 focus:outline-none focus:border-palm" />
      </div>
      <input placeholder="Skills (teaching, health, media, coaching…)" value={f.skills} onChange={set("skills")} data-testid="vol-skills" className="mt-3 w-full rounded-md border border-ink/15 px-3 py-2.5 focus:outline-none focus:border-palm" />
      <input placeholder="Availability" value={f.availability} onChange={set("availability")} data-testid="vol-availability" className="mt-3 w-full rounded-md border border-ink/15 px-3 py-2.5 focus:outline-none focus:border-palm" />
      <textarea rows={3} placeholder="Anything else we should know?" value={f.message} onChange={set("message")} data-testid="vol-message" className="mt-3 w-full rounded-md border border-ink/15 px-3 py-2.5 focus:outline-none focus:border-palm" />
      <button disabled={loading} data-testid="volunteer-submit-btn" className="mt-5 w-full bg-mango text-palm-deep font-bold rounded-full px-6 py-3.5 hover:shadow-xl hover:-translate-y-0.5 transition disabled:opacity-60">
        {loading ? "Sending…" : "Raise your hand →"}
      </button>
    </form>
  );
}

function PartnerForm() {
  const [f, setF] = useState({ organization: "", contact_name: "", email: "", phone: "", partnership_type: "", message: "" });
  const [loading, setLoading] = useState(false);
  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/partner", f);
      toast.success("Received — HOFA's partnerships team will reach out.");
      setF({ organization: "", contact_name: "", email: "", phone: "", partnership_type: "", message: "" });
    } catch {
      toast.error("Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  }
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  return (
    <form id="partner" onSubmit={submit} className="bg-white border border-ink/10 rounded-[14px] p-8" data-testid="partner-form">
      <span className="text-xs uppercase tracking-[0.14em] font-bold text-clay">Partner</span>
      <h3 className="font-display text-2xl mt-2 mb-5">Weave with us.</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <input required placeholder="Organization" value={f.organization} onChange={set("organization")} data-testid="partner-org" className="rounded-md border border-ink/15 px-3 py-2.5 focus:outline-none focus:border-palm" />
        <input required placeholder="Contact person" value={f.contact_name} onChange={set("contact_name")} data-testid="partner-contact" className="rounded-md border border-ink/15 px-3 py-2.5 focus:outline-none focus:border-palm" />
        <input required type="email" placeholder="Email" value={f.email} onChange={set("email")} data-testid="partner-email" className="rounded-md border border-ink/15 px-3 py-2.5 focus:outline-none focus:border-palm" />
        <input placeholder="Phone" value={f.phone} onChange={set("phone")} data-testid="partner-phone" className="rounded-md border border-ink/15 px-3 py-2.5 focus:outline-none focus:border-palm" />
      </div>
      <input placeholder="Type of partnership" value={f.partnership_type} onChange={set("partnership_type")} data-testid="partner-type" className="mt-3 w-full rounded-md border border-ink/15 px-3 py-2.5 focus:outline-none focus:border-palm" />
      <textarea required rows={4} placeholder="Tell us about the collaboration you're imagining" value={f.message} onChange={set("message")} data-testid="partner-message" className="mt-3 w-full rounded-md border border-ink/15 px-3 py-2.5 focus:outline-none focus:border-palm" />
      <button disabled={loading} data-testid="partner-submit-btn" className="mt-5 w-full bg-clay text-white font-bold rounded-full px-6 py-3.5 hover:shadow-xl hover:-translate-y-0.5 transition disabled:opacity-60">
        {loading ? "Sending…" : "Start a conversation →"}
      </button>
    </form>
  );
}

function PledgeForm() {
  const [f, setF] = useState({ name: "", email: "", phone: "", monthly_amount: 25, start_month: "", note: "" });
  const [loading, setLoading] = useState(false);
  const AMOUNTS = [10, 25, 50, 100];
  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/pledge", { ...f, monthly_amount: Number(f.monthly_amount) });
      toast.success("Pledge received. HOFA will reach out to set up monthly giving.");
      setF({ name: "", email: "", phone: "", monthly_amount: 25, start_month: "", note: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  }
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  return (
    <form id="pledge" onSubmit={submit} className="bg-white border border-ink/10 rounded-[14px] p-8" data-testid="pledge-form">
      <span className="text-xs uppercase tracking-[0.14em] font-bold text-berry">Monthly pledge</span>
      <h3 className="font-display text-2xl mt-2 mb-3">Give every month.</h3>
      <p className="text-ink/70 text-sm mb-5">
        Commit to a monthly amount and HOFA's team will follow up with a secure setup link. Cancel any time.
      </p>

      <div className="flex flex-wrap gap-2 mb-4">
        {AMOUNTS.map((a) => {
          const active = Number(f.monthly_amount) === a;
          return (
            <button
              type="button"
              key={a}
              onClick={() => setF((s) => ({ ...s, monthly_amount: a }))}
              data-testid={`pledge-amount-${a}`}
              className={`px-4 py-2 rounded-full font-bold text-sm border-2 transition ${
                active ? "bg-berry text-white border-berry" : "bg-white text-ink border-ink/15 hover:border-berry"
              }`}
            >
              ${a}/mo
            </button>
          );
        })}
        <input
          type="number"
          min="1"
          step="1"
          placeholder="Custom $"
          value={f.monthly_amount}
          onChange={set("monthly_amount")}
          data-testid="pledge-amount-input"
          className="w-32 rounded-full border-2 border-ink/15 px-4 py-2 text-sm focus:outline-none focus:border-berry"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <input required placeholder="Full name" value={f.name} onChange={set("name")} data-testid="pledge-name" className="rounded-md border border-ink/15 px-3 py-2.5 focus:outline-none focus:border-berry" />
        <input required type="email" placeholder="Email" value={f.email} onChange={set("email")} data-testid="pledge-email" className="rounded-md border border-ink/15 px-3 py-2.5 focus:outline-none focus:border-berry" />
        <input placeholder="Phone (optional)" value={f.phone} onChange={set("phone")} data-testid="pledge-phone" className="rounded-md border border-ink/15 px-3 py-2.5 focus:outline-none focus:border-berry" />
        <input placeholder="Start month (e.g. Feb 2026)" value={f.start_month} onChange={set("start_month")} data-testid="pledge-start" className="rounded-md border border-ink/15 px-3 py-2.5 focus:outline-none focus:border-berry" />
      </div>
      <textarea rows={2} placeholder="Anything HOFA should know?" value={f.note} onChange={set("note")} data-testid="pledge-note" className="mt-3 w-full rounded-md border border-ink/15 px-3 py-2.5 focus:outline-none focus:border-berry" />
      <button disabled={loading} data-testid="pledge-submit-btn" className="mt-5 w-full bg-berry text-white font-bold rounded-full px-6 py-3.5 hover:shadow-xl hover:-translate-y-0.5 transition disabled:opacity-60">
        {loading ? "Sending pledge…" : "Pledge monthly →"}
      </button>
      <p className="text-xs text-ink/50 mt-3">
        Pledges are captured as intent. HOFA's team follows up personally to set up a secure monthly Stripe subscription — auto-charging is not enabled from this form.
      </p>
    </form>
  );
}

export default function GetInvolved() {
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
    }
  }, [hash]);
  return (
    <>
      <SEO title="Get Involved · Donate, Volunteer, Partner" description="Give, volunteer, or partner with HOFA — three ways to weave your work into our community programs across District 17, Liberia." path="/get-involved" />
      <section className="pt-24 pb-14">
        <div className="max-w-[1140px] mx-auto px-6">
          <Eyebrow>Join the work</Eyebrow>
          <h1 className="text-[clamp(38px,5vw,60px)] mb-4 max-w-[24ch] font-display font-bold">
            Every thread strengthens the cloth.
          </h1>
          <p className="text-ink/75 max-w-[62ch]">
            Give, volunteer, or partner — three ways to weave your work into HOFA's community programs across District 17 and beyond.
          </p>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-[1140px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-[1.1fr_.9fr] gap-8">
          <DonationForm />
          <PledgeForm />
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-[1140px] mx-auto px-6 rounded-[14px] p-8 text-white" style={{ background: "linear-gradient(160deg,#123B23,#1F5C38)" }}>
          <span className="text-xs uppercase tracking-[0.14em] font-bold text-mango">Where your gift goes</span>
          <h3 className="font-display text-2xl mt-2 mb-4">Directly into community programs.</h3>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 text-white/85">
            <li className="flex gap-3"><span className="mt-2 w-2 h-2 bg-mango rounded-sm shrink-0" /> Computer literacy training (MDGCLP)</li>
            <li className="flex gap-3"><span className="mt-2 w-2 h-2 bg-indigo2 rounded-sm shrink-0" /> Community research through CMFRII</li>
            <li className="flex gap-3"><span className="mt-2 w-2 h-2 bg-clay rounded-sm shrink-0" /> Documentary storytelling (HOFAVIPROC)</li>
            <li className="flex gap-3"><span className="mt-2 w-2 h-2 bg-palm rounded-sm shrink-0" /> ESAFC youth football development</li>
            <li className="flex gap-3"><span className="mt-2 w-2 h-2 bg-berry rounded-sm shrink-0" /> Health outreach across District 17 (COHEDIP)</li>
          </ul>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-[1140px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-8">
          <VolunteerForm />
          <PartnerForm />
        </div>
      </section>
    </>
  );
}
